/* ==========================================================================
   Search modal — <search-modal> custom element.
   Open/close with focus trap + body-scroll lock, debounced predictive fetch via
   the Section Rendering API, keyboard navigation across all result groups, and
   per-viewer recent searches. Depends on trapFocus / removeTrapFocus / debounce
   from global.js. Falls back to /search when JS or the section is unavailable.
   ========================================================================== */
if (!customElements.get('search-modal')) {
  class SearchModal extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('.search-modal__dialog');
      this.input = this.querySelector('.search-modal__input');
      this.form = this.querySelector('form');
      this.results = this.querySelector('[data-search-modal-results]');
      this.empty = this.querySelector('[data-search-modal-empty]');
      this.status = this.querySelector('[data-search-modal-status]');
      this.clearBtn = this.querySelector('.search-modal__clear');
      this.recentGroup = this.querySelector('[data-search-modal-recent]');
      this.viewedGroup = this.querySelector('[data-search-modal-recently-viewed]');
      this.cache = new Map();
      this.controller = null;
      this.lastTrigger = null;
      this.recentKey = 'inspired:recent-searches';
      this.viewedKey = 'inspired:recently-viewed';
      this.recordCurrentProduct();

      this.querySelectorAll('[data-search-modal-close]').forEach((el) =>
        el.addEventListener('click', () => this.close())
      );
      // Any click that lands outside the dialog closes the modal, even if the
      // backdrop element is hidden by a global rule (base.css div:empty).
      this.addEventListener('click', (e) => {
        if (e.target === this || !this.dialog.contains(e.target)) this.close();
      });

      const run = typeof debounce === 'function' ? debounce(() => this.onInput(), 250) : () => this.onInput();
      this.input.addEventListener('input', run);
      this.addEventListener('keydown', (e) => this.onKeydown(e));
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
      this.addEventListener('click', (e) => this.onRowClick(e));
      const recentClear = this.querySelector('[data-search-modal-recent-clear]');
      if (recentClear) recentClear.addEventListener('click', () => this.clearRecent());

      if (this.dataset.shortcut === 'true') {
        this.onShortcut = (e) => {
          if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
            e.preventDefault();
            this.isOpen() ? this.close() : this.open();
          }
        };
        document.addEventListener('keydown', this.onShortcut);
      }

      if (window.Shopify && window.Shopify.designMode) {
        this.onSectionSelect = (e) => {
          if (e.detail && e.detail.sectionId === this.dataset.sectionId) this.open();
        };
        this.onSectionDeselect = (e) => {
          if (e.detail && e.detail.sectionId === this.dataset.sectionId) this.close();
        };
        document.addEventListener('shopify:section:select', this.onSectionSelect);
        document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
      }
    }

    /* The theme editor replaces the section's DOM on every setting change. If the
       modal was open at that moment, release everything it holds on the document
       (scroll lock, body class, focus trap, document listeners) so the new instance
       and the rest of the page start clean. */
    disconnectedCallback() {
      if (this.onShortcut) document.removeEventListener('keydown', this.onShortcut);
      if (this.onSectionSelect) document.removeEventListener('shopify:section:select', this.onSectionSelect);
      if (this.onSectionDeselect) document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
      if (this.controller) this.controller.abort();
      if (this.closeTimer) clearTimeout(this.closeTimer);
      if (this.isOpen()) {
        this.unlockScroll();
        document.body.classList.remove('search-modal--open');
        if (typeof removeTrapFocus === 'function') removeTrapFocus();
      }
    }

    isOpen() {
      return this.hasAttribute('open');
    }

    open() {
      if (this.isOpen()) return;
      if (this.closeTimer) { clearTimeout(this.closeTimer); this.closeTimer = null; }
      this.classList.remove('is-closing');
      // The theme's page-transition script adds body.unloading to every same-host
      // <a> click (global.js). Our trigger is an <a href="/search"> whose navigation
      // we cancel, so lift the class again or the transition cover never goes away.
      document.body.classList.remove('unloading');
      this.setAttribute('open', '');
      this.lockScroll();
      document.body.classList.add('search-modal--open');
      this.renderRecent();
      this.renderRecentlyViewed();
      // display:none -> flex and .is-visible in the same style pass would skip the
      // transition; flush layout first so the browser sees the start state.
      void this.dialog.offsetHeight;
      this.classList.add('is-visible');
      if (typeof trapFocus === 'function') trapFocus(this.dialog, this.input);
      else this.input.focus();
      this.dispatchEvent(new CustomEvent('searchmodal:open', { bubbles: true }));
    }

    close() {
      if (!this.isOpen()) return;
      this.classList.add('is-closing');
      this.classList.remove('is-visible');
      const finish = () => {
        this.removeAttribute('open');
        this.classList.remove('is-closing');
        this.dialog.removeEventListener('transitionend', onEnd);
        clearTimeout(this.closeTimer);
        this.closeTimer = null;
      };
      const onEnd = (e) => {
        if (e.target === this.dialog && e.propertyName === 'opacity') finish();
      };
      this.dialog.addEventListener('transitionend', onEnd);
      this.closeTimer = setTimeout(finish, 400);
      this.unlockScroll();
      document.body.classList.remove('search-modal--open');
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.lastTrigger);
      this.dispatchEvent(new CustomEvent('searchmodal:close', { bubbles: true }));
    }

    lockScroll() {
      const sw = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.setProperty('--scrollbar-width', `${sw}px`);
      document.body.classList.add('overflow-hidden');
    }

    unlockScroll() {
      document.body.classList.remove('overflow-hidden');
      document.documentElement.style.removeProperty('--scrollbar-width');
    }

    toggleClear(show) {
      if (this.clearBtn) this.clearBtn.hidden = !show; // legacy hook; the clear button was removed
    }

    onInput() {
      const q = this.input.value.trim();
      this.toggleClear(q.length > 0);
      if (!q) return this.showEmptyState();
      if (this.dataset.predictive !== 'true') return;
      this.fetchResults(q);
    }

    showEmptyState() {
      if (this.results) {
        this.results.hidden = true;
        this.results.innerHTML = '';
      }
      if (this.empty) this.empty.hidden = false;
      this.input.setAttribute('aria-expanded', 'false');
      this.clearSelection();
    }

    async fetchResults(q) {
      const key = q.toLowerCase();
      if (this.cache.has(key)) return this.renderResults(this.cache.get(key));
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      this.setAttribute('loading', '');
      this.announce(this.dataset.loadingText || '');

      const params = new URLSearchParams();
      params.set('q', q);
      params.set('section_id', this.dataset.sectionId);
      params.set('resources[type]', this.dataset.resources || 'product');
      params.set('resources[limit]', this.dataset.limit || '4');
      params.set('resources[limit_scope]', 'each');
      params.set('resources[options][unavailable_products]', this.dataset.unavailable || 'last');
      params.set('resources[options][fields]', 'title,product_type,variants.title,variants.sku,vendor,tag');

      const base = this.dataset.predictiveUrl || '/search/suggest';
      try {
        const res = await fetch(`${base}?${params.toString()}`, { signal: this.controller.signal });
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const node = doc.querySelector('[data-search-modal-results]');
        const markup = node ? node.innerHTML : '';
        this.cache.set(key, markup);
        if (this.cache.size > 50) this.cache.clear();
        this.renderResults(markup);
      } catch (e) {
        if (e.name !== 'AbortError') this.showEmptyState();
      } finally {
        this.removeAttribute('loading');
      }
    }

    renderResults(markup) {
      if (!this.results) return;
      this.results.innerHTML = markup;
      this.results.hidden = false;
      if (this.empty) this.empty.hidden = true;
      this.input.setAttribute('aria-expanded', 'true');
      this.clearSelection();
      const count = this.results.querySelectorAll('.search-modal__row').length;
      this.announce(`${count} results`);
    }

    activeContainer() {
      return this.results && !this.results.hidden ? this.results : this.empty;
    }

    options() {
      const c = this.activeContainer();
      if (!c) return [];
      return Array.from(c.querySelectorAll('[role="option"]'));
    }

    clearSelection() {
      this.options().forEach((o) => o.removeAttribute('aria-selected'));
      this.input.removeAttribute('aria-activedescendant');
    }

    move(dir) {
      const opts = this.options();
      if (!opts.length) return;
      let i = opts.findIndex((o) => o.getAttribute('aria-selected') === 'true');
      opts.forEach((o) => o.removeAttribute('aria-selected'));
      i = dir === 'down' ? (i + 1) % opts.length : (i - 1 + opts.length) % opts.length;
      const sel = opts[i];
      sel.setAttribute('aria-selected', 'true');
      if (sel.id) this.input.setAttribute('aria-activedescendant', sel.id);
      const link = sel.querySelector('a') || sel;
      link.scrollIntoView({ block: 'nearest' });
    }

    onKeydown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.close();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.move('down');
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.move('up');
      } else if (e.key === 'Enter') {
        const sel = this.options().find((o) => o.getAttribute('aria-selected') === 'true');
        if (sel) {
          e.preventDefault();
          const link = sel.querySelector('a');
          if (link) link.click();
        }
      }
    }

    onRowClick(e) {
      const termRow = e.target.closest('[data-search-term]');
      if (termRow) {
        const term = termRow.getAttribute('data-search-term');
        const href = termRow.getAttribute('href');
        // Popular-search rows carry a real /search?q= href: record the term and
        // let the browser navigate to the results page. (global.js already added
        // body.unloading on click; the resulting page load clears it.) Recent-search
        // rows use href="#" and stay in the modal.
        if (href && href !== '#') {
          this.saveRecent(term);
          return;
        }
        e.preventDefault();
        this.input.value = term;
        this.toggleClear(true);
        this.saveRecent(term);
        this.onInput();
        this.input.focus();
        return;
      }
      const link = e.target.closest('a.search-modal__row');
      if (link && !link.hasAttribute('data-search-term') && link.getAttribute('href') !== '#') {
        this.saveRecent(this.input.value.trim());
      }
    }

    onSubmit(e) {
      const q = this.input.value.trim();
      if (!q) {
        e.preventDefault();
        return;
      }
      this.saveRecent(q);
    }

    announce(text) {
      if (this.status) this.status.textContent = text;
    }

    /* ---------------- recent searches (localStorage) ---------------- */
    readRecent() {
      try {
        return JSON.parse(window.localStorage.getItem(this.recentKey)) || [];
      } catch (e) {
        return [];
      }
    }

    saveRecent(term) {
      if (this.dataset.recent !== 'true' || !term) return;
      try {
        const limit = parseInt(this.dataset.recentLimit, 10) || 5;
        let list = this.readRecent().filter((t) => t.toLowerCase() !== term.toLowerCase());
        list.unshift(term);
        list = list.slice(0, limit);
        window.localStorage.setItem(this.recentKey, JSON.stringify(list));
      } catch (e) {
        /* storage unavailable — skip */
      }
    }

    renderRecent() {
      if (!this.recentGroup || this.dataset.recent !== 'true') return;
      const list = this.readRecent();
      const ul = this.recentGroup.querySelector('.search-modal__list');
      if (!list.length) {
        this.recentGroup.hidden = true;
        if (ul) ul.innerHTML = '';
        return;
      }
      ul.innerHTML = list
        .map((term) => {
          const safe = term.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
          return `<li role="option"><a class="search-modal__row" href="#" data-search-term="${safe}"><span class="search-modal__row-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg></span><span class="search-modal__row-label">${safe}</span></a></li>`;
        })
        .join('');
      this.recentGroup.hidden = false;
    }

    clearRecent() {
      try {
        window.localStorage.removeItem(this.recentKey);
      } catch (e) {
        /* ignore */
      }
      this.renderRecent();
    }

    /* ---------------- recently viewed products (localStorage) ----------------
       The section prints the current product as JSON on product templates; we
       push it to the front of a capped per-viewer list and render it on open. */
    readViewed() {
      try {
        const list = JSON.parse(window.localStorage.getItem(this.viewedKey)) || [];
        return Array.isArray(list) ? list.filter((p) => p && p.handle && p.title && p.url) : [];
      } catch (e) {
        return [];
      }
    }

    recordCurrentProduct() {
      if (this.dataset.recentlyViewed !== 'true') return;
      const node = this.querySelector('[data-search-modal-current-product]');
      if (!node) return;
      try {
        const product = JSON.parse(node.textContent);
        if (!product || !product.handle) return;
        let list = this.readViewed().filter((p) => p.handle !== product.handle);
        list.unshift({ handle: product.handle, title: product.title, url: product.url, image: product.image || '', price: product.price || '' });
        list = list.slice(0, 12); // keep a few more than shown so the limit setting can grow later
        window.localStorage.setItem(this.viewedKey, JSON.stringify(list));
      } catch (e) {
        /* storage unavailable — skip */
      }
    }

    renderRecentlyViewed() {
      if (!this.viewedGroup || this.dataset.recentlyViewed !== 'true') return;
      const limit = parseInt(this.dataset.recentlyViewedLimit, 10) || 4;
      const current = this.querySelector('[data-search-modal-current-product]');
      let currentHandle = null;
      try { currentHandle = current ? JSON.parse(current.textContent).handle : null; } catch (e) { /* ignore */ }
      const list = this.readViewed().filter((p) => p.handle !== currentHandle).slice(0, limit);
      const ul = this.viewedGroup.querySelector('.search-modal__list');
      if (!list.length) {
        this.viewedGroup.hidden = true;
        if (ul) ul.innerHTML = '';
        return;
      }
      const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
      const showImage = this.dataset.showImage === 'true';
      const showPrice = this.dataset.showPrice === 'true';
      ul.innerHTML = list
        .map((p) => {
          const media = showImage
            ? `<span class="search-modal__row-media">${p.image ? `<img src="${esc(p.image)}" alt="" width="40" height="40" loading="lazy">` : ''}</span>`
            : '';
          const sub = showPrice && p.price ? `<span class="search-modal__row-price"><span class="search-modal__row-price-current">${esc(p.price)}</span></span>` : '';
          return `<li role="option"><a class="search-modal__row search-modal__row--product" href="${esc(p.url)}">${media}<span class="search-modal__row-text"><span class="search-modal__row-title">${esc(p.title)}</span>${sub}</span></a></li>`;
        })
        .join('');
      this.viewedGroup.hidden = false;
    }
  }

  customElements.define('search-modal', SearchModal);
}

/* Delegated trigger: any [data-search-modal-trigger] opens the modal if present,
   otherwise the link falls through to /search. */
if (!window.__searchModalTriggerBound) {
  window.__searchModalTriggerBound = true; // the editor re-executes this file on every section re-render
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-search-modal-trigger]');
    if (!trigger) return;
    const modal = document.querySelector('search-modal');
    if (!modal || typeof modal.open !== 'function') return;
    e.preventDefault();
    modal.lastTrigger = trigger;
    modal.open();
  });
}
